<template>
  <v-toolbar card color="transparent">
    <v-toolbar-side-icon>
      <v-icon class="grey--text">mdi-comment-question-outline</v-icon>
    </v-toolbar-side-icon>
    <v-toolbar-title class="subheading grey--text">
      Questionnaire ({{ questions.length }} questions)
    </v-toolbar-title>

    <v-spacer />

    <can I="edit" a="Opportunity">
      <v-menu left max-width="30vw" v-model="removing">
        <v-btn flat icon slot="activator" color="error">
          <v-icon size="18px">mdi-delete</v-icon>
        </v-btn>

        <v-card>
          <v-card-title class="headline">Are you sure?</v-card-title>
          <v-card-text>
            <p>
              This will remove all questions and all responses from candidates and cannot be
              reversed.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-btn flat block @click="removing = false" color="grey">
              cancel
            </v-btn>
            <v-btn block @click="remove" color="error">
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </can>

    <v-btn flat icon @click="previewing = true" color="secondary">
      <v-icon size="18px">mdi-eye</v-icon>
    </v-btn>

    <can I="edit" a="Opportunity">
      <v-btn flat icon @click="editing = true">
        <v-icon size="18px">mdi-pencil</v-icon>
      </v-btn>
    </can>

    <can I="edit" a="Opportunity">
      <v-dialog v-model="previewing" max-width="60vw">
        <v-card dark>
          <v-card-title class="title">
            Questionnaire Preview
          </v-card-title>
          <v-list>
            <v-list-tile
              v-for="question in questions"
              :key="`question-tile-${question.position}`"
              @click=""
              class="py-1"
            >
              <v-list-tile-content>
                <v-list-tile-title>
                  {{ question.position }}. {{ question.title }}
                </v-list-tile-title>
                <v-list-tile-sub-title>
                  {{ question.optional ? 'Question can be ignored' : 'A response is required' }}
                  {{ question.wordLimit ? `(max ${question.wordLimit} words)` : '' }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-icon v-if="question.questionType === 'text'">mdi-message-text</v-icon>
                <v-icon v-if="question.questionType === 'file'">mdi-paperclip</v-icon>
                <v-icon v-if="question.questionType === 'choice'"
                  >mdi-format-list-bulleted-type</v-icon
                >
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
          <v-card-actions
            ><v-spacer />
            <v-btn flat @click="previewing = false">
              Dismiss
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </can>

    <can I="edit" a="Opportunity">
      <v-dialog v-model="editing" max-width="700" @input="cancel">
        <v-card dark>
          <v-card-title class="title">
            Questions
          </v-card-title>
          <v-card-text>
            <v-form v-model="valid">
              <draggable v-model="questions" @end="afterDrag" :options="{ handle: '.move-handle' }">
                <v-card
                  class="mb-2"
                  color="grey darken-2"
                  v-for="question in questions"
                  :key="`question-${question.position}`"
                >
                  <v-card-title class="move-handle subheading">
                    {{ question.position }}.&nbsp;
                    <span v-if="question.id">{{ question.title }}</span>
                    <span v-else>New question</span>
                    <v-spacer />
                    <v-btn small flat icon @click="removeQuestion(question)" color="error">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                    <v-icon color="accent lighten-2">mdi-cursor-move</v-icon>
                  </v-card-title>
                  <v-card-text>
                    <v-text-field label="Question" v-model="question.title" />
                    <v-switch
                      :label="
                        question.optional ? 'Question can be ignored' : 'A response is required'
                      "
                      v-model="question.optional"
                    />
                    <v-select
                      v-model="question.questionType"
                      label="Type"
                      :items="[
                        { value: 'text', text: 'Text response' },
                        { value: 'choice', text: 'Multiple choice' },
                        { value: 'file', text: 'File upload' },
                      ]"
                    />
                    <v-combobox
                      v-if="question.questionType === 'choice'"
                      multiple
                      chips
                      :items="[]"
                      label="Choices"
                      v-model="question.options"
                    />
                    <v-combobox
                      v-if="autoProgress && question.questionType != 'file'"
                      multiple
                      chips
                      :items="[]"
                      label="Preferred Answers"
                      v-model="question.preferredAnswers"
                    />
                    <v-switch
                      v-if="question.questionType === 'choice'"
                      label="Allow options other than those specified above"
                      v-model="question.allowOther"
                    />
                    <v-text-field
                      v-if="question.questionType === 'text'"
                      v-model.number="question.wordLimit"
                      label="Word limit"
                      type="number"
                    />
                    <v-switch label="Include in exports?" v-model="question.exportable" />
                    <v-text-field
                      v-if="question.exportable"
                      label="Column heading in exports"
                      v-model="question.exportableLabel"
                    />
                  </v-card-text>
                </v-card>
              </draggable>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn @click="addQuestion" color="secondary">
              <v-icon left>mdi-plus</v-icon> Question
            </v-btn>
            <v-spacer />
            <v-switch label="Automatic progression?" v-model="autoProgress" />
            <v-spacer />
            <v-btn flat @click="cancel" color="grey">cancel</v-btn>
            <v-btn @click="submit" color="primary">
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </can>
  </v-toolbar>
</template>

<script>
// Components
import Markdown from 'vue-markdown'
import Draggable from 'vuedraggable'

// Vue Component
export default {
  components: { Markdown, Draggable },
  props: {
    value: { type: Object },
  },
  created() {
    if (!this.value.id) this.$store.dispatch('admin/opportunities/createBehavior', this.value)
  },
  data() {
    return {
      // Toggles
      editing: false,
      previewing: false,
      removing: false,
      autoProgress: this.value.configuration['autoProgress'] || false,
      // Model
      behavior: Object.assign({}, this.value),
      questions: (this.value.questions || [])
        .map(q => Object.assign({}, q))
        .sort((a, b) => a.position - b.position),
      deleted: [],
      // Validations
      valid: true,
    }
  },
  methods: {
    addQuestion() {
      this.questions.push({
        id: null,
        optional: false,
        title: '',
        questionType: 'text',
        options: [],
        preferredAnswers: [],
        allowOther: false,
        wordLimit: null,
        position:
          this.questions.length > 0 ? Math.max(...this.questions.map(q => q.position)) + 1 : 1, // append
      })
    },
    removeQuestion(question) {
      if (question.id) this.deleted.push(question.id)
      this.questions = this.questions.filter(q => q !== question)
    },
    afterDrag() {
      this.questions.forEach((q, i) => (q.position = i + 1))
    },
    cancel() {
      this.editing = false
      this.$store
        .dispatch('admin/opportunities/fetch', this.$route.params.id)
        .then(this.resetQuestions)
    },
    submit() {
      this.$store
        .dispatch('admin/opportunities/setQuestions', {
          questionnaire: this.behavior,
          questions: this.questions,
          autoProgress: this.autoProgress,
          deleted: this.deleted,
        })
        .then(() => {
          this.editing = false
          this.$store
            .dispatch('admin/opportunities/fetch', this.$route.params.id)
            .then(this.resetQuestions)
        })
    },
    resetQuestions() {
      this.deleted = []
      this.questions = this.value.questions
        .map(q => Object.assign({}, q))
        .sort((a, b) => a.position - b.position)
    },
    remove() {
      this.$store.dispatch('admin/opportunities/removeBehavior', this.behavior)
    },
  },
}
</script>
